/* eslint-disable no-console */
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React, { useState } from 'react';

import { uploadFileWithProgress } from '@actions/file';

import Icon from '@components/Icon/Icon';

import {
  useCreateDiscernDocumentFromUploadMutation,
  ListDiscernDocumentsDocument,
} from 'generated/discern.generated';
import {
  useCompleteFhirFileUploadMutation,
  useInitiateFhirFileUploadMutation,
} from 'generated/file.generated';

interface DiscernUploadProps {
  vitallPatientId?: string;
  patientName?: string;
  onUploadComplete?: () => void;
}

const DiscernUpload: React.FC<DiscernUploadProps> = ({
  vitallPatientId,
  patientName,
  onUploadComplete,
}) => {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [initiatePdf] = useInitiateFhirFileUploadMutation();
  const [completePdf] = useCompleteFhirFileUploadMutation();
  const [createDiscernDocument] = useCreateDiscernDocumentFromUploadMutation({
    refetchQueries: vitallPatientId
      ? [
          {
            query: ListDiscernDocumentsDocument,
            variables: {
              input: {
                vitall_patient_id: vitallPatientId,
              },
            },
          },
        ]
      : [],
    awaitRefetchQueries: true,
  });
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'application/pdf') {
      alert('Please upload a PDF file.');
      e.target.value = '';
      return;
    }

    // Get client ID
    const clientId = vitallPatientId || '';
    if (!clientId) {
      alert('Patient ID is required');
      return;
    }

    // Automatically start upload
    await handleUpload(selectedFile, clientId);
  };

  const handleUpload = async (file: File, vitallPatientId: string) => {
    setIsUploading(true);
    setProgress(0);

    try {
      const presignedResponse = await initiatePdf({
        variables: {
          input: {
            name: file.name,
            contentType: file.type,
            size: file.size,
          },
        },
      });

      const session = presignedResponse.data!.initiateFhirFileUpload;

      // Upload PDF to S3 with progress
      const uploadedParts = await uploadFileWithProgress(
        file,
        session.parts,
        (loaded, total) => {
          const percentComplete = Math.round((loaded / total) * 100);
          setProgress(percentComplete);
        },
        session.expectedHeaders
      );

      // Complete the multipart upload to finalize the file in S3
      // This is needed to get binaryId for PDF preview
      const multipartUploadInfo = {
        isMultipart: session.is_multipart,
        multipartUploadId: session.multipart_upload_id,
        parts: uploadedParts,
      };

      // Complete FHIR upload to get binaryId for PDF preview
      const completeResponse = await completePdf({
        variables: {
          input: {
            session_id: session.session_id,
            multipartUploadInfo,
          },
        },
      });

      if (completeResponse.errors) {
        throw new Error(`Upload failed: ${completeResponse.errors[0].message}`);
      }

      const binaryId = completeResponse.data?.completeFhirFileUpload;

      if (!binaryId) {
        throw new Error('Failed to get binary ID from upload');
      }

      // Create Discern document immediately so it appears in the table with "New" status
      const documentResponse = await createDiscernDocument({
        variables: {
          input: {
            vitall_patient_id: vitallPatientId,
            binary_resource_id: binaryId,
            filename: file.name,
          },
        },
      });

      const documentId =
        documentResponse.data?.createDiscernDocumentFromUpload
          .discern_document_id;

      // Navigate to split editor with binaryId and documentId
      const queryParams = new URLSearchParams({
        binaryId: binaryId,
        vitallPatientId: vitallPatientId,
        patientName: patientName || '',
        fileName: file.name,
      });
      if (documentId) {
        queryParams.set('discernDocumentId', documentId);
      }
      const url = `/discern/split-editor?${queryParams.toString()}`;
      navigate(url);

      onUploadComplete?.();
    } catch (error: any) {
      console.error('Upload error:', error);
      alert('Upload failed. Check console for details.');
    } finally {
      setIsUploading(false);
      setProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Stack spacing={2.5}>
      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        accept="application/pdf"
        type="file"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Upload Progress */}
      {isUploading && (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ flex: 1, height: 8, borderRadius: 1 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 45 }}
            >
              {progress}%
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Upload Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleFileSelect}
          disabled={isUploading}
          startIcon={<Icon name="cloud-arrow-up" type="fas" />}
        >
          {isUploading ? 'Uploading...' : 'Upload Records'}
        </Button>
      </Box>
    </Stack>
  );
};

export default DiscernUpload;
